/**
 * The countdown.
 *
 * Every value here comes from the server's `deadline_ms` via the ServerClock —
 * the local wall clock is never consulted, so changing the system time does not
 * buy a single second.
 *
 * Tabular figures are not decorative: a countdown whose width shifts as digits
 * change is a distraction in the corner of the eye for three hours.
 */
import { AlertTriangle, Clock } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatDuration } from "@/lib/exam/serverClock";

export function ExamTimer({
  remainingMs,
  critical,
}: {
  remainingMs: number | null;
  /** Under five minutes. */
  critical: boolean;
}) {
  if (remainingMs === null) {
    return (
      <span className="text-xs text-ink-faint">Waiting for the timer…</span>
    );
  }

  const expired = remainingMs <= 0;

  return (
    <div
      role="timer"
      aria-live="off"
      className={cn(
        "flex items-center gap-1.5 rounded-md border px-2 py-1",
        expired || critical
          ? "border-review/40 bg-review-soft"
          : "border-border bg-surface",
      )}
    >
      {critical || expired ? (
        <AlertTriangle className="size-3.5 text-review" />
      ) : (
        <Clock className="size-3.5 text-ink-faint" />
      )}
      <span className="text-2xs text-ink-muted">
        {expired ? "Time up" : "Time left"}
      </span>
      <span
        className={cn(
          "tabular text-sm font-medium",
          critical || expired ? "text-review" : "text-ink",
        )}
      >
        {formatDuration(remainingMs)}
      </span>
    </div>
  );
}
